import { X } from 'lucide-react'
import * as React from 'react'

import { useIsMobile } from '../hooks/use-mobile'
import { cn } from '../lib/utils'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './dialog'
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from './drawer'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: React.ReactNode
  description?: React.ReactNode
  children?: React.ReactNode
  footer?: React.ReactNode
  closeOnOverlayClick?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  className?: string
  contentClassName?: string
  showCloseButton?: boolean
}

const Modal: React.FC<ModalProps> & {
  Header: typeof ModalHeader
  Body: typeof ModalBody
  Footer: typeof ModalFooter
} = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  closeOnOverlayClick = true,
  size = 'md',
  className,
  contentClassName,
  showCloseButton = true
}) => {
  const isDesktop = !useIsMobile()

  const sizeClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    full: 'sm:max-w-[95vw]'
  }

  const closeButton = showCloseButton && (
    <button
      onClick={onClose}
      className={cn(
        'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background',
        'transition-opacity hover:opacity-100',
        'focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2',
        'disabled:pointer-events-none'
      )}
    >
      <X className="h-4 w-4" />
      <span className="sr-only">Close</span>
    </button>
  )

  if (isDesktop) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open && closeOnOverlayClick) {
            onClose()
          }
        }}
        modal
      >
        <DialogContent
          className={cn('gap-0 p-0', sizeClasses[size], showCloseButton ? '' : '[&>button]:hidden', contentClassName)}
        >
          {closeButton}
          {(title || description) && (
            <DialogHeader className={cn('p-4', !title && !description && 'sr-only')}>
              <DialogTitle>{title || 'Dialog'}</DialogTitle>
              {description && <DialogDescription>{description}</DialogDescription>}
            </DialogHeader>
          )}
          <div className={cn('p-4', className)}>{children}</div>
          {footer && <DialogFooter className="p-4">{footer}</DialogFooter>}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(setOpen) => {
        if (!setOpen && closeOnOverlayClick) {
          onClose()
        }
      }}
    >
      <DrawerContent className="px-0">
        {closeButton}
        {(title || description) && (
          <DrawerHeader className={cn('p-4', !title && !description && 'sr-only')}>
            <DrawerTitle>{title || 'Dialog'}</DrawerTitle>
            {description && <DrawerDescription>{description}</DrawerDescription>}
          </DrawerHeader>
        )}
        <div className={cn('p-4', className)}>{children}</div>
        {footer && <DrawerFooter className="p-4">{footer}</DrawerFooter>}
      </DrawerContent>
    </Drawer>
  )
}

const ModalHeader: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ className, children }) => {
  const isDesktop = !useIsMobile()

  if (isDesktop) {
    return <DialogHeader className={cn('p-4', className)}>{children}</DialogHeader>
  }
  return <DrawerHeader className={cn('p-4', className)}>{children}</DrawerHeader>
}

const ModalBody: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ className, children }) => <div className={cn('p-4', className)}>{children}</div>

const ModalFooter: React.FC<{
  className?: string
  children: React.ReactNode
}> = ({ className, children }) => {
  const isDesktop = !useIsMobile()

  if (isDesktop) {
    return <DialogFooter className={cn('p-4', className)}>{children}</DialogFooter>
  }
  return <DrawerFooter className={cn('p-4', className)}>{children}</DrawerFooter>
}

Modal.Header = ModalHeader
Modal.Body = ModalBody
Modal.Footer = ModalFooter

export { Modal }
export type { ModalProps }
